import React from 'react';
import {
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { Cached } from '@mui/icons-material';

import { AvatarSetting, PhoneNumberMask, PhoneVerified } from 'components';
import { EUserRole } from 'enums';
import { selectCurrentUserInfo } from 'store/slices/auth.slice';
import { useAppSelector } from 'store/store.hook';
import { useGenerateEmployeeCodeQuery } from 'store/apis';

import { useUserSettingsFormManager } from './user-settings-form.hook';

interface UserSettingsFormProps {
  isMobile?: boolean;
  userId: number;
  onInit?: (loading: boolean) => void;
  onSubmitting?: (loading: boolean) => void;
  next?: (data?: any) => void;
}

export const UserSettingsForm = React.forwardRef(
  ({ isMobile = false, userId, onInit = () => {}, onSubmitting = () => {}, next }: UserSettingsFormProps, ref) => {
    const { orgSlug, id: currentUserId } = useAppSelector(selectCurrentUserInfo);
    const [{ user, formManager, loading_form, loading_submit }] = useUserSettingsFormManager({ userId, next });

    React.useImperativeHandle(ref, () => ({ onSubmit: () => formManager.handleSubmit() }), []);
    React.useEffect(() => onInit(loading_form), [loading_form]);
    React.useEffect(() => onSubmitting(loading_submit), [loading_submit]);

    const [skipGenerateEmployeeCode, setSkipGenerateEmployeeCode] = React.useState<boolean>(true);
    const { data: { code = '' } = {}, isLoading: employeeCodeGenerating } = useGenerateEmployeeCodeQuery(
      { orgSlug },
      { skip: skipGenerateEmployeeCode },
    );

    React.useEffect(() => {
      if (!employeeCodeGenerating && code) {
        formManager.setFieldValue('code', code);
        setSkipGenerateEmployeeCode(true);
      }
    }, [employeeCodeGenerating, code]);

    const isLoggedInUser = user?.id === currentUserId;

    return (
      <form onSubmit={formManager.handleSubmit} autoComplete="off">
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <AvatarSetting
              avatarUrl={
                formManager.values.avatar ? URL.createObjectURL(formManager.values.avatar) : user?.profile_photo?.url
              }
              onSelectFile={(file) => formManager.setFieldValue('avatar', file)}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Full Name"
              variant="outlined"
              fullWidth
              value={formManager.values.name}
              onChange={formManager.handleChange('name')}
              error={!!(formManager.errors.name && formManager.touched.name)}
              helperText={formManager.touched.name ? formManager.errors.name : null}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Email Address"
              variant="outlined"
              fullWidth
              value={formManager.values.email}
              onChange={formManager.handleChange('email')}
              error={!!(formManager.errors.email && formManager.touched.email)}
              helperText={formManager.touched.email ? formManager.errors.email : null}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Phone Number"
              placeholder="(000) 000-0000"
              variant="outlined"
              fullWidth
              value={formManager.values.phone}
              name="phone"
              InputProps={{
                inputComponent: PhoneNumberMask as any,
              }}
              onChange={formManager.handleChange('phone')}
              error={!!(formManager.errors.phone && formManager.touched.phone)}
              helperText={formManager.touched.phone ? formManager.errors.phone : null}
            />
            {user && user.phone_verified_at === null && <PhoneVerified user={user} />}
          </Grid>

          <Grid item sx={isMobile ? { flex: 1 } : { width: '50%' }}>
            <TextField
              label="Employee Code"
              variant="outlined"
              fullWidth
              disabled
              value={formManager.values.code}
              onChange={formManager.handleChange('code')}
              error={!!(formManager.errors.code && formManager.touched.code)}
              helperText={formManager.touched.code ? formManager.errors.code : null}
            />
          </Grid>

          <Grid item sx={isMobile ? {} : { width: '50%' }}>
            <Button
              startIcon={
                employeeCodeGenerating ? (
                  <CircularProgress
                    color="inherit"
                    size={14}
                    sx={{
                      marginRight: isMobile ? 0 : 0.8,
                    }}
                  />
                ) : (
                  <Cached />
                )
              }
              sx={{
                paddingX: isMobile ? 0 : 2,
                marginTop: 1.4,
                ...(isMobile ? { width: 30, height: 30, minWidth: 30, paddingLeft: 1.25 } : {}),
              }}
              onClick={() => {
                setSkipGenerateEmployeeCode((prev) => !prev);
              }}
            >
              {isMobile ? '' : 'Generate Code'}
            </Button>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="body2" sx={{ marginBottom: 1 }}>
              User Role
            </Typography>

            <FormControl sx={{ width: '100%' }}>
              <RadioGroup
                row
                defaultValue={EUserRole.NO_ACCESS}
                value={formManager.values.role}
                onChange={(e) => formManager.setFieldValue('role', e.target.value)}
                sx={{
                  flexDirection: isMobile ? 'column' : 'row',
                  justifyContent: 'space-between',
                }}
              >
                <FormControlLabel
                  disabled={isLoggedInUser}
                  control={<Radio value={EUserRole.BASIC} />}
                  label={
                    <Typography variant="body2" color="textSecondary">
                      Basic
                    </Typography>
                  }
                />

                <FormControlLabel
                  disabled={isLoggedInUser}
                  control={<Radio value={EUserRole.ADMIN} />}
                  label={
                    <Typography variant="body2" color="textSecondary">
                      Admin
                    </Typography>
                  }
                />

                <FormControlLabel
                  disabled
                  control={<Radio value={EUserRole.NO_ACCESS} />}
                  label={
                    <Typography variant="body2" color="textSecondary">
                      No Access
                    </Typography>
                  }
                />
              </RadioGroup>
            </FormControl>
          </Grid>

          <Grid item xs={12} hidden>
            <Button
              type="submit"
              variant="contained"
              fullWidth
              sx={{
                color: 'white',
              }}
            >
              Submit
            </Button>
          </Grid>
        </Grid>
      </form>
    );
  },
);
